<template>
  <div class="penalty-display" :class="`team-${team.toLowerCase()}`">
    <div class="penalties-container">
      <!-- Red 3s Section - Simple display with -500 badge -->
      <div v-if="red3Count > 0" class="penalty-section red-threes">
        <div class="red-three-cards">
          <div
            v-for="i in displayedRed3s"
            :key="`red3-${i}`"
            class="red-three-card"
            :class="{ flipped: flippedCards.includes(i) }"
            :style="getRed3Style(i)"
          >
            <div class="card-inner">
              <!-- Card back (before flip) -->
              <div class="card-back-wrapper">
                <Card :card="null" :faceDown="true" size="small" />
              </div>
              <!-- Card front (red 3 - revealed after flip) -->
              <div class="card-front-wrapper">
                <Card :card="getRed3Card(i)" size="small" />
              </div>
            </div>
            <div class="penalty-badge">-500</div>
          </div>
        </div>
      </div>

      <!-- Hand Cards Section - Enhanced with Card Fans when player data available -->
      <div v-if="handPenalty > 0" class="penalty-section hand-cards">
        <!-- Enhanced view with per-player card fans -->
        <TeamPenaltiesDisplay
          v-if="hasPlayerHands"
          :team="team"
          :team-name="teamName || `Team ${team}`"
          :player-hands="playerHands"
          :total-penalty="handPenalty"
          :opponent-total-penalty="opponentPenalty"
          :is-animating="isAnimating"
          @animation-complete="onHandPenaltyAnimationComplete"
        />

        <!-- Fallback simple view when no player data -->
        <template v-else>
          <div class="penalty-header">
            <span class="penalty-label">Cards in Hand</span>
          </div>

          <div
            class="hand-penalty-display"
            :class="{ 'shake-in': showHandPenalty, visible: showHandPenalty }"
          >
            <div class="hand-icon">
              <span class="material-symbols-outlined">back_hand</span>
            </div>
            <div class="hand-info">
              <span class="hand-count">{{ handCardCount }} cards remaining</span>
            </div>
            <div class="hand-penalty-value">
              <span class="total-value penalty">-{{ handPenalty.toLocaleString() }}</span>
            </div>
          </div>
        </template>
      </div>

      <!-- No Penalties Message -->
      <div v-if="red3Count === 0 && handPenalty === 0 && goingOutBonus === 0" class="no-penalties">
        <span class="material-symbols-outlined">check_circle</span>
        <span>No adjustments!</span>
      </div>

      <!-- Going Out Bonus (only for winning team) -->
      <div v-if="goingOutBonus > 0" class="bonus-section going-out-bonus" :class="{ 'show': showGoingOutBonus }">
        <div class="bonus-display">
          <div class="bonus-icon">
            <span class="material-symbols-outlined">emoji_events</span>
          </div>
          <div class="bonus-info">
            <span class="bonus-label">Going Out Bonus</span>
          </div>
          <div class="bonus-value">
            <span class="total-value bonus">+{{ goingOutBonus.toLocaleString() }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useRoundEndAnimations } from '../../composables/useRoundEndAnimations'
import { useSounds } from '../../composables/useSounds'
import Card from '../cards/Card.vue'
import TeamPenaltiesDisplay from './TeamPenaltiesDisplay.vue'

// ============================================================================
// Types
// ============================================================================

interface CardData {
  rank: string
  suit: string
}

interface PlayerHandData {
  id: string
  username: string
  display_name?: string
  team: 'A' | 'B'
  seat: number
  is_bot: boolean
  hand: CardData[]
  foot: CardData[]
}

interface Props {
  team: 'A' | 'B'
  red3Count: number
  handPenalty: number
  handCardCount?: number
  basePoints?: number
  isAnimating?: boolean
  showMath?: boolean
  naturalCanastasCanceled?: number
  // Going out bonus (100 points for team that went out)
  goingOutBonus?: number
  // New props for enhanced display
  playerHands?: PlayerHandData[]
  teamName?: string
  opponentPenalty?: number
}

const props = withDefaults(defineProps<Props>(), {
  handCardCount: 0,
  basePoints: 0,
  isAnimating: false,
  showMath: false,
  naturalCanastasCanceled: 0,
  goingOutBonus: 0,
  playerHands: () => [],
  teamName: '',
  opponentPenalty: 0,
})

const emit = defineEmits<{
  penaltyComplete: [total: number]
}>()

const { generateNaturalPosition } = useRoundEndAnimations()
const { playPenalty } = useSounds()

// Animation state
const displayedRed3s = ref(0)
const flippedCards = ref<number[]>([])
const showHandPenalty = ref(false)
const showGoingOutBonus = ref(false)

// Computed
const totalPenalty = computed(() => props.red3Count * 500 + props.handPenalty)
const finalTotal = computed(() => Math.max(0, props.basePoints - totalPenalty.value))

// Check if we have player hand data for enhanced display
const hasPlayerHands = computed(() => {
  return props.playerHands && props.playerHands.length > 0
})

// Random positions for red 3 cards
const red3Positions = ref<Map<number, { rotation: number; offsetX: number; offsetY: number }>>(
  new Map()
)

// Initialize red 3 positions
function initRed3Positions(): void {
  red3Positions.value.clear()

  for (let i = 1; i <= props.red3Count; i++) {
    const pos = generateNaturalPosition(0, 0, i, false)
    red3Positions.value.set(i, {
      rotation: pos.rotation,
      offsetX: pos.x,
      offsetY: pos.y,
    })
  }
}

// Get style for red 3 card
function getRed3Style(index: number): Record<string, string> {
  const pos = red3Positions.value.get(index)

  if (!pos) {
    return { animationDelay: `${(index - 1) * 0.2}s` }
  }

  return {
    transform: `rotate(${pos.rotation}deg) translate(${pos.offsetX}px, ${pos.offsetY}px)`,
    animationDelay: `${(index - 1) * 0.2}s`,
  }
}

// Get card data for red 3 (alternating hearts/diamonds)
function getRed3Card(index: number): { rank: string; suit: string } {
  return {
    rank: '3',
    suit: index % 2 === 0 ? 'hearts' : 'diamonds',
  }
}

// Animate red 3s appearing with flip reveal
async function animateRed3s(): Promise<void> {
  for (let i = 1; i <= props.red3Count; i++) {
    // Card appears face-down first
    displayedRed3s.value = i
    await delay(100) // Brief pause before flip

    // Flip to reveal
    flippedCards.value.push(i)
    playPenalty()
    await delay(200) // Brisk flip timing (was 300ms)
  }
}

// Delay helper
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

// Handler for TeamPenaltiesDisplay animation completion
function onHandPenaltyAnimationComplete(): void {
  // The TeamPenaltiesDisplay component handles its own animations
  // Just continue with the total animation
  showHandPenalty.value = true
}

// Initialize on mount
onMounted(() => {
  initRed3Positions()

  if (!props.isAnimating) {
    displayedRed3s.value = props.red3Count
    flippedCards.value = Array.from({ length: props.red3Count }, (_, i) => i + 1)
    showHandPenalty.value = props.handPenalty > 0
    showGoingOutBonus.value = props.goingOutBonus > 0
  }
})

// Watch for animation trigger - immediate: true ensures it fires on mount
watch(
  () => props.isAnimating,
  async (animating) => {
    if (animating) {
      displayedRed3s.value = 0
      flippedCards.value = []
      showHandPenalty.value = false
      showGoingOutBonus.value = false

      await delay(200) // Brisk initial delay

      if (props.red3Count > 0) {
        await animateRed3s()
        await delay(200) // Brief pause after red 3s
      }

      if (props.handPenalty > 0) {
        if (hasPlayerHands.value) {
          // TeamPenaltiesDisplay handles its own animations
          // Wait for card fan animations (2 players x 500ms delay + reveal time)
          const estimatedAnimTime = props.playerHands.length * 600 + 800
          await delay(estimatedAnimTime)
        } else {
          // Simple fallback display
          showHandPenalty.value = true
          playPenalty()
          await delay(400) // Shake animation duration
        }
      }

      // Show going out bonus after penalties (gold celebration style)
      if (props.goingOutBonus > 0) {
        await delay(300)
        showGoingOutBonus.value = true
        await delay(500) // Let the animation play
      }

      emit('penaltyComplete', totalPenalty.value)
    }
  },
  { immediate: true }
)

// Expose for parent orchestration
defineExpose({
  animateRed3s,
})
</script>

<style scoped>
/* Minimal container - no heavy borders */
.penalty-display {
  padding: 0.5rem;
}

.penalties-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.penalty-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
}

.penalty-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.penalty-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
}

.penalty-each {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
}

.red-three-cards {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  justify-content: center;
}

.red-three-card {
  position: relative;
  width: 55px;
  height: 77px;
  perspective: 600px;
  animation: red3Appear 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) backwards;
}

@keyframes red3Appear {
  from {
    opacity: 0;
    transform: scale(0.5) translateY(20px);
  }
  to {
    opacity: 1;
  }
}

.card-inner {
  position: relative;
  width: 100%;
  height: 100%;
  transition: transform 0.4s ease-out;
  transform-style: preserve-3d;
}

.red-three-card.flipped .card-inner {
  transform: rotateY(180deg);
}

.card-back-wrapper,
.card-front-wrapper {
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
}

.card-front-wrapper {
  transform: rotateY(180deg);
}

.penalty-badge {
  position: absolute;
  bottom: -8px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(220, 38, 38, 0.9);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  color: white;
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
  font-size: 0.625rem;
  font-weight: 700;
  z-index: 10;
  border: 1px solid rgba(239, 68, 68, 0.4);
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.4);
}

.hand-penalty-display {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.5rem;
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(220, 38, 38, 0.05));
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 10px;
  opacity: 0;
  transform: translateX(-100px);
}

.hand-penalty-display.visible {
  opacity: 1;
  transform: translateX(0);
}

.hand-penalty-display.shake-in {
  animation: shakeIn 0.5s cubic-bezier(0.36, 0.07, 0.19, 0.97);
}

@keyframes shakeIn {
  0% {
    transform: translateX(-100px) rotate(-10deg);
    opacity: 0;
  }
  20% {
    transform: translateX(20px) rotate(5deg);
    opacity: 1;
  }
  40% {
    transform: translateX(-15px) rotate(-3deg);
  }
  60% {
    transform: translateX(10px) rotate(2deg);
  }
  80% {
    transform: translateX(-5px) rotate(-1deg);
  }
  100% {
    transform: translateX(0) rotate(0);
    opacity: 1;
  }
}

.hand-icon {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.3), rgba(220, 38, 38, 0.2));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.hand-icon .material-symbols-outlined {
  font-size: 1.75rem;
  color: #fca5a5;
}

.hand-info {
  flex: 1;
}

.hand-count {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
}

.hand-penalty-value {
  flex-shrink: 0;
}

.penalty-total {
  text-align: center;
}

.total-value {
  font-size: 1.25rem;
  font-weight: 700;
}

.total-value.penalty {
  color: #f87171;
}

.no-penalties {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1.5rem;
  color: #10b981;
}

.no-penalties .material-symbols-outlined {
  font-size: 1.5rem;
}

/* Going Out Bonus Styles */
.bonus-section.going-out-bonus {
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.bonus-section.going-out-bonus.show {
  opacity: 1;
  transform: translateY(0);
}

.bonus-display {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.5rem;
  background: linear-gradient(135deg, rgba(232, 197, 71, 0.15), rgba(212, 175, 55, 0.08));
  border: 1px solid rgba(232, 197, 71, 0.4);
  border-radius: 10px;
  box-shadow: 0 0 20px rgba(232, 197, 71, 0.2);
}

.bonus-icon {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(232, 197, 71, 0.4), rgba(212, 175, 55, 0.3));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.bonus-icon .material-symbols-outlined {
  font-size: 1.75rem;
  color: #e8c547;
}

.bonus-info {
  flex: 1;
}

.bonus-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}

.bonus-value {
  flex-shrink: 0;
}

.total-value.bonus {
  color: #e8c547;
  text-shadow: 0 0 10px rgba(232, 197, 71, 0.5);
}
</style>
